import { Box, Dialog, Stack, Typography, alpha, styled } from '@mui/material';
import { GOLD_ACCENT } from 'src/components/buyCreditsDialog/styles/index.styles';
import {
  GOLD_DEEP,
  GOLD_INK,
  GOLD_LIGHT,
  bodyText,
} from 'src/components/careerProPitch/sheet.styles';
import PressableButton from 'src/components/pressableButton';

/**
 * Dressed as the Pro pass it is about: the Pro pitch's gold-and-white sheet,
 * its gold coin, and the platform's raised buttons. The golds are fixed, as on
 * the pitch; surfaces and text follow the theme into dark mode.
 */

export const NoticeDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '22px',
    margin: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(4)})`,
    overflow: 'hidden',
    border: `1px solid ${alpha(GOLD_DEEP, 0.35)}`,
    boxShadow: `0 24px 60px ${alpha(GOLD_DEEP, 0.28)}`,
  },
}));

export const Content = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3.5, 3, 3),
  gap: theme.spacing(2.25),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2.25, 2.5),
  },
}));

export const Header = styled(Stack)({
  alignItems: 'center',
  textAlign: 'center',
});

// A raised gold coin, lit from the top left — the Pro mark the buyer is
// about to lose, with the stopped autopay pinned to it.
export const GoldCoin = styled(Box)({
  position: 'relative',
  width: 68,
  height: 68,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: GOLD_INK,
  background: `radial-gradient(circle at 32% 26%, ${GOLD_LIGHT}, ${GOLD_ACCENT} 52%, ${GOLD_DEEP})`,
  boxShadow: `inset 0 2px 0 ${alpha('#fff', 0.65)}, 0 5px 0 ${GOLD_DEEP}, 0 14px 28px ${alpha(GOLD_DEEP, 0.45)}`,
  '& > svg': { fontSize: 34 },
});

export const CoinBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: -4,
  bottom: -2,
  width: 26,
  height: 26,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.warning.main,
  backgroundColor: theme.palette.background.paper,
  boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.18)}`,
  '& > svg': { fontSize: 16 },
}));

export const BodyText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.55,
  ...bodyText(theme),
}));

// What "No, it was a mistake" will do, spelled out before the buyer picks it.
export const RestoreCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  padding: theme.spacing(1.75, 2),
  border: `1px solid ${alpha(GOLD_DEEP, 0.4)}`,
  background: `linear-gradient(160deg, ${alpha(GOLD_ACCENT, 0.2)}, ${alpha(GOLD_ACCENT, 0.06)})`,
}));

export const RestoreCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: theme.palette.brand.goldInk,
  marginBottom: theme.spacing(1.25),
}));

export const FactRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  '& + &': { marginTop: theme.spacing(1.25) },
}));

export const FactIcon = styled(Box)({
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: GOLD_INK,
  background: `linear-gradient(145deg, ${GOLD_LIGHT}, ${GOLD_ACCENT})`,
  boxShadow: `0 3px 0 ${GOLD_DEEP}`,
  '& > svg': { fontSize: 18 },
});

export const FactText = styled(Typography)(({ theme }) => ({
  fontSize: 13.5,
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.text.primary,
  '& strong': { fontWeight: 700 },
}));

export const FootNote = styled(Typography)(({ theme }) => ({
  fontSize: 11.5,
  textAlign: 'center',
  ...bodyText(theme),
}));

export const Question = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  fontWeight: 700,
  textAlign: 'center',
  color: theme.palette.primary.main,
}));

// Room under the row for the buttons' raised edge and glow.
export const ActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column-reverse',
  gap: theme.spacing(1.75),
  paddingBottom: '6px',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

// `&&&` to sit above PressableButton's own and the theme's contained rules.
export const AnswerButton = styled(PressableButton)(({ theme }) => ({
  '&&&': {
    width: '100%',
    height: 50,
    fontSize: 15,
    fontWeight: 700,
    [theme.breakpoints.up('sm')]: {
      flex: 1,
      width: 'auto',
    },
  },
}));
